import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';

const MyAccount = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    email: '',
    billingAddress: {
      street: '',
      city: '',
      postalCode: '',
      country: ''
    },
    vatNumber: '',
    personalNumber: ''
  });
  const [wonCars, setWonCars] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProfile = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const token = await user.getIdToken();
          const response = await fetch('/api/userProfile', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await response.json();
          setUserProfile(data);
          setFormData({
            firstName: data.firstName,
            lastName: data.lastName,
            dateOfBirth: data.dateOfBirth.split('T')[0],
            phoneNumber: data.phoneNumber,
            email: data.email,
            billingAddress: data.billingAddress,
            vatNumber: data.vatNumber,
            personalNumber: data.personalNumber
          });
        } catch (err) {
          setError('Chyba při načítání profilu.');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    const fetchWonCars = async () => {
      const user = auth.currentUser;
      if (!user) {
        console.log('Uživatel není přihlášen');
        return;
      }

      try {
        const token = await user.getIdToken();
        const auctionsResponse = await fetch('/api/auctions/won', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!auctionsResponse.ok) {
          throw new Error('Nepodařilo se načíst vyhraná auta');
        }

        const wonAuctions = await auctionsResponse.json();
        setWonCars(wonAuctions);
      } catch (err) {
        console.error('Chyba při načítání vyhraných aut:', err);
        setError('Chyba při načítání vyhraných aut.');
      }
    };

    fetchWonCars();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      billingAddress: {
        ...prev.billingAddress,
        [name]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/updateUserProfile', {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Nepodařilo se aktualizovat profil.');
        }

        const updatedProfile = await response.json();
        alert(updatedProfile.message);
        setUserProfile(updatedProfile.profile);
      } catch (err) {
        setError(err.message);
      }
    }
  };

  if (loading) return <div>Načítání...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto p-6 bg-gray-800 rounded-lg">
        <h2 className="text-2xl font-bold mb-4 text-white">Můj účet</h2>
        <div className="flex flex-col md:flex-row gap-6">
          <form onSubmit={handleSubmit} className="space-y-4 w-full md:w-1/2">
            <div>
              <label className="block text-white">Jméno:</label>
              <input 
                type="text" 
                name="firstName" 
                value={formData.firstName} 
                onChange={handleChange} 
                className="border rounded p-2 w-full"
                required
              />
            </div>
            <div>
              <label className="block text-white">Příjmení:</label>
              <input 
                type="text" 
                name="lastName" 
                value={formData.lastName} 
                onChange={handleChange} 
                className="border rounded p-2 w-full"
                required
              />
            </div>
            <div>
              <label className="block text-white">Datum narození:</label>
              <input 
                type="date" 
                name="dateOfBirth" 
                value={formData.dateOfBirth} 
                onChange={handleChange} 
                className="border rounded p-2 w-full"
                required
              />
            </div>
            <div>
              <label className="block text-white">Telefonní číslo:</label>
              <input 
                type="text" 
                name="phoneNumber" 
                value={formData.phoneNumber} 
                onChange={handleChange} 
                className="border rounded p-2 w-full"
                required
              />
            </div>
            <div>
              <label className="block text-white">E-mail:</label>
              <input 
                type="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                className="border rounded p-2 w-full"
                required
              />
            </div>
            <div>
              <label className="block text-white">IČ:</label>
              <input 
                type="text" 
                name="vatNumber" 
                value={formData.vatNumber} 
                onChange={handleChange} 
                className="border rounded p-2 w-full"
              />
            </div>
            <div>
              <label className="block text-white">Rodné číslo:</label>
              <input 
                type="text" 
                name="personalNumber" 
                value={formData.personalNumber} 
                onChange={handleChange} 
                className="border rounded p-2 w-full"
              />
            </div>
            <button 
              type="submit" 
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
            >
              Uložit změny
            </button>
          </form>

          <div className="w-full md:w-1/2">
            <h3 className="text-lg font-semibold mt-4 text-white">Vyhraná auta</h3>
            {wonCars.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {wonCars.map(auction => (
                  <div key={auction._id} className="bg-gray-700 rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-xl font-semibold mb-2 text-white">
                          {auction.carId.make} {auction.carId.model}
                        </h4>
                        <div className="space-y-1 text-gray-300">
                          <p><span className="font-medium">Rok výroby:</span> {auction.carId.year}</p>
                          <p><span className="font-medium">Najeto:</span> {auction.carId.mileage.toLocaleString()} km</p>
                          <p><span className="font-medium">Motor:</span> {auction.carId.engineCapacity} ccm</p>
                          <p><span className="font-medium">Palivo:</span> {auction.carId.fuelType}</p>
                          <p><span className="font-medium">VIN:</span> {auction.carId.vin}</p>
                          <p className="text-green-600 font-semibold mt-2">
                            Vyhráno za: {auction.currentBid.toLocaleString()} Kč
                          </p>
                          <p className="text-sm text-gray-500">
                            Datum výhry: {new Date(auction.updatedAt).toLocaleDateString('cs-CZ')}
                          </p>
                          <p className="text-sm text-blue-500">
                            Status aukce: {auction.status}
                          </p>
                        </div>
                      </div>
                      {auction.carId.images && auction.carId.images.length > 0 && (
                        <img 
                          src={auction.carId.images[0]} 
                          alt={`${auction.carId.make} ${auction.carId.model}`} 
                          className="w-32 h-32 object-cover rounded-lg"
                        />
                      )}
                    </div>
                    <div className="mt-4 flex justify-end">
                      <a
                        href={`/car/${auction.carId._id}`}
                        className="text-blue-500 hover:text-blue-600 font-medium"
                      >
                        Zobrazit detail →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <p className="text-gray-600">
                  Zatím jste nevyhráli žádnou aukci.
                </p>
                <a 
                  href="/auctions" 
                  className="inline-block mt-4 text-blue-500 hover:text-blue-600 font-medium"
                >
                  Prozkoumat dostupné aukce →
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAccount; 